import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";
import PinLogin from "@/components/PinLogin";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [mode, setMode] = useState(() => (localStorage.getItem("kindred_pin_identifier") ? "pin" : "password"));
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onLoggedIn = (data) => {
    login(data.token, data.user);
    toast.success(`Welcome back, ${data.user.name.split(" ")[0]}!`);
    navigate(data.user.role === "admin" ? "/admin" : "/dashboard");
  };
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password, remember });
      onLoggedIn(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="text-center mb-8">
        <span className="w-14 h-14 rounded-2xl bg-[#D96B43] inline-flex items-center justify-center shadow-sm">
          <Heart className="w-7 h-7 text-white" fill="white" />
        </span>
        <h1 className="text-3xl font-display font-bold text-slate-900 mt-4">Welcome back</h1>
        <p className="text-slate-600 mt-1">Log in to your Kindred account</p>
      </div>
      <div data-testid="judge-banner" className="mb-5 bg-[#FFF8E6] border-2 border-dashed border-[#F3D98B] rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-slate-800"><span className="font-semibold text-[#A9741A]">Demo for judges:</span> try <strong>seeker@test.com</strong> with PIN <strong>2580</strong> — no password needed.</p>
        <button type="button" data-testid="judge-banner-btn" onClick={() => { localStorage.setItem("kindred_pin_identifier", "seeker@test.com"); setMode("pin"); window.dispatchEvent(new Event("kindred-pin-prefill")); }}
          className="bg-[#D96B43] hover:bg-[#C25832] text-white font-semibold px-4 min-h-[44px] rounded-xl transition-colors">Use demo login</button>
      </div>
      <div className="flex gap-2 mb-5 bg-white border border-[#E2D9CE] rounded-2xl p-1.5">
        {[["pin", "4-digit PIN"], ["password", "Email & password"]].map(([v, l]) => (
          <button key={v} type="button" data-testid={`login-mode-${v}`} onClick={() => setMode(v)}
            className={`flex-1 min-h-[52px] rounded-xl text-lg font-semibold transition-colors ${mode === v ? "bg-[#2E7D6B] text-white" : "text-slate-700 hover:bg-[#FAF8F5]"}`}>{l}</button>
        ))}
      </div>
      {mode === "pin" ? (
        <>
          <PinLogin onSuccess={onLoggedIn} />
          <p className="text-center text-slate-600 mt-4">No PIN yet? Sign in with your password, then set one in your Profile.</p>
        </>
      ) : (
      <form onSubmit={submit} className="bg-white border border-[#E2D9CE] rounded-2xl p-6 sm:p-8 space-y-5 shadow-sm">
        <div>
          <label className="block font-semibold text-slate-800 mb-2">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
        </div>
        <div>
          <label className="block font-semibold text-slate-800 mb-2">Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 min-h-[52px] rounded-xl border border-[#E2D9CE] focus:border-[#D96B43] focus:ring-2 focus:ring-[#D96B43]/20 outline-none" />
        </div>
        <label className="flex items-center gap-3 text-lg text-slate-800 cursor-pointer min-h-[48px]">
          <input data-testid="login-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="w-6 h-6 accent-[#2E7D6B]" />
          Keep me signed in on this device (30 days)
        </label>
        <button data-testid="login-submit" disabled={loading}
          className="w-full bg-[#D96B43] hover:bg-[#C25832] disabled:opacity-60 text-white font-semibold min-h-[52px] rounded-xl text-lg transition-colors">
          {loading ? "Logging in…" : "Log in"}
        </button>
        <div className="flex items-center gap-3 text-slate-400 text-sm"><span className="flex-1 h-px bg-[#E2D9CE]" />or<span className="flex-1 h-px bg-[#E2D9CE]" /></div>
        <GoogleButton label="Log in with Google" />
      </form>
      )}
      <p className="text-center text-slate-600 mt-6">
        New to Kindred? <Link to="/register" className="text-[#D96B43] font-semibold">Create an account</Link>
      </p>
    </div>
  );
}
